/**
 * Barramento de eventos do jogo.
 *
 * Regra de ouro do projeto: sistemas NAO se importam diretamente uns aos outros
 * para reagir a acontecimentos. Quem faz algo emite; quem se interessa escuta.
 * Isso e o que permite que cada sistema seja escrito de forma independente.
 *
 * Os nomes de evento validos estao em EVENTS (abaixo). Nao invente nomes novos
 * sem adiciona-los la - o registro serve de contrato entre os sistemas.
 */

export const EVENTS = {
  // --- ciclo de jogo ---
  GAME_STATE: 'game:state',            // { state: 'menu'|'playing'|'paused'|'gameover'|'victory' }
  WAVE_START: 'game:wave:start',       // { wave, enemies }
  WAVE_CLEAR: 'game:wave:clear',       // { wave }

  // --- armas ---
  WEAPON_FIRE: 'weapon:fire',          // { weapon, origin, dir, ammoLeft }
  WEAPON_DRY: 'weapon:dry',            // { weapon }  (gatilho sem municao)
  WEAPON_RELOAD_START: 'weapon:reload:start', // { weapon, duration }
  WEAPON_RELOAD_END: 'weapon:reload:end',     // { weapon }
  WEAPON_SWITCH: 'weapon:switch',      // { weapon, previous }
  WEAPON_ADS: 'weapon:ads',            // { weapon, active }

  // --- balistica / combate ---
  BULLET_IMPACT: 'bullet:impact',      // { point, normal, surface, object, fromPlayer }
  ENTITY_DAMAGED: 'entity:damaged',    // { entity, amount, part, point, source, weapon }
  ENTITY_KILLED: 'entity:killed',      // { entity, source, weapon, headshot }
  PLAYER_DAMAGED: 'player:damaged',    // { amount, from: Vector3, health }
  PLAYER_HEALED: 'player:healed',      // { health }
  PLAYER_DIED: 'player:died',          // { source }

  // --- movimento / presenca ---
  FOOTSTEP: 'entity:footstep',         // { position, surface, isPlayer, running }
  PLAYER_LAND: 'player:land',          // { position, surface, impact }
  PLAYER_JUMP: 'player:jump',          // { position }

  // --- IA ---
  AI_FIRE: 'ai:fire',                  // { bot, origin, dir }
  AI_ALERT: 'ai:alert',                // { bot, position }

  // --- feedback de interface ---
  HITMARKER: 'ui:hitmarker',           // { headshot, killed }
  KILLFEED: 'ui:killfeed',             // { killer, victim, weapon, headshot }
  NOTICE: 'ui:notice',                 // { text, duration }

  // --- audio ---
  SFX: 'audio:sfx',                    // { name, position, volume, pitch }

  // --- rede / multijogador ---
  // Acrescentados junto com o modo online. Mesma regra dos de cima: quem faz
  // emite, quem se interessa escuta. Nenhum sistema de rede chama a UI direto.
  NET_STATUS: 'net:status',            // { state:'offline'|'connecting'|'online'|'error', message }
  NET_ERROR: 'net:error',              // { code, message }
  REMOTE_SHOT: 'net:remoteshot',       // { by, origin, dir, weapon }

  // --- partida online ---
  MATCH_JOINED: 'match:joined',        // { room, players, you }
  MATCH_LEFT: 'match:left',            // { reason }
  MATCH_SCORE: 'match:score',          // { players, timeLeft }
  MATCH_END: 'match:end',              // { standings, matchId }

  // --- conta ---
  AUTH_CHANGED: 'auth:changed',        // { user }  (user null = deslogado)

  // --- entrada por toque ---
  TOUCH_ACTIVE: 'ui:touch'             // { active }
};

export class EventBus {
  constructor() {
    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();
    this._muted = false;
  }

  /**
   * Registra um ouvinte. Devolve uma funcao que o remove (util em dispose()).
   * @returns {() => void}
   */
  on(type, fn) {
    let set = this._listeners.get(type);
    if (!set) {
      set = new Set();
      this._listeners.set(type, set);
    }
    set.add(fn);
    return () => this.off(type, fn);
  }

  once(type, fn) {
    const off = this.on(type, (payload) => {
      off();
      fn(payload);
    });
    return off;
  }

  off(type, fn) {
    const set = this._listeners.get(type);
    if (set) set.delete(fn);
  }

  /**
   * Dispara o evento. Erros de um ouvinte nunca derrubam os outros nem o frame:
   * sao logados e o laco continua.
   */
  emit(type, payload) {
    if (this._muted) return;
    const set = this._listeners.get(type);
    if (!set || set.size === 0) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[EventBus] ouvinte de "${type}" lancou erro:`, err);
      }
    }
  }

  clear() {
    this._listeners.clear();
  }
}
